import {
  Breadcrumbs,
  AvatarWithName,
  MediaQuery,
  mq,
  Text,
} from "@certn/cascadia-react";
import { parseName } from "@certn/utils";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { LegacyCaseActionsMenu } from "../../group-overview/components/LegacyCaseActionsMenu";
import { renderLegacyStatusTag } from "../../../utils/renderLegacyStatusTag";

interface LegacyProfileCardProps {
  reportId: string;
  requestorName?: string | null;
  status?: string | null;
  score?: string | null;
  created?: string;
  modified?: string;
  applicantEmail?: string | null;
}

function LegacyProfileCard({
  reportId,
  requestorName,
  status,
  score,
  created,
  modified,
  applicantEmail,
}: LegacyProfileCardProps) {
  const { t } = useTranslation();

  return (
    <div className={styles.profileCard}>
      {renderLegacyStatusTag({
        status,
        score,
      })}

      <div className={styles.innerProfileCard}>
        <MediaQuery sm md type="show">
          <Text className={css({ whiteSpace: "nowrap" })}>
            {t("Case ID: ") + reportId}
          </Text>
        </MediaQuery>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "var(--space-4)",
            alignItems: "center",
          }}
        >
          {applicantEmail && (
            <Text style="global/body-secondary">{applicantEmail}</Text>
          )}
          {requestorName && (
            <Text style="global/body-secondary" className={styles.requestor}>
              {t("Requestor")}:
              <AvatarWithName
                gap="space-1"
                name={parseName(requestorName || undefined)}
              />
            </Text>
          )}
        </div>
      </div>
    </div>
  );
}

interface LegacyCaseDetailsTitleCardProps {
  caseId?: string;
  reportId: string;
  applicantFirstName?: string | null;
  applicantLastName?: string | null;
  applicantEmail?: string | null;
  groupName?: string | null;
  ownerName?: string | null;
  requestedByName?: string | null;
  status?: string | null;
  score?: string | null;
  teamType?: "HR" | "PM" | null;
  created?: string;
  modified?: string;
}

export function LegacyCaseDetailsTitleCard({
  caseId,
  reportId,
  applicantFirstName,
  applicantLastName,
  applicantEmail,
  groupName,
  ownerName,
  requestedByName,
  status,
  score,
  teamType,
  created,
  modified,
}: LegacyCaseDetailsTitleCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();

  const displayName =
    applicantFirstName && applicantLastName
      ? `${applicantFirstName} ${applicantLastName}`
      : applicantEmail;

  return (
    <div>
      <div className={styles.container}>
        <div className={styles.infoContainer}>
          <Breadcrumbs
            breadcrumbs={[
              {
                navigate: () => navigate(state?.last?.href || "/cases/legacy"),
                title: groupName || "-",
              },
              {
                title: t("Legacy Case Details"),
              },
            ]}
          />
          <div className={styles.titleRow}>
            <Text
              as="h1"
              style="global/title-medium"
              testID="heading"
              className={styles.titleText}
            >
              {displayName || "-"}
            </Text>
            {caseId && (
              <div className={styles.menuContainer}>
                <LegacyCaseActionsMenu
                  caseId={caseId}
                  reportId={reportId}
                  teamType={teamType || "HR"}
                  isInDetailsPage={true}
                />
              </div>
            )}
          </div>
          <LegacyProfileCard
            reportId={reportId}
            requestorName={requestedByName}
            status={status}
            score={score}
            created={created}
            modified={modified}
            applicantEmail={applicantEmail}
          />
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: css(
    {
      display: "flex",
      width: "100%",
      justifyContent: "space-between",
      backgroundColor: "var(--surface-base)",
    },
    mq({
      alignItems: ["normal", "normal", "center"],
      flexDirection: ["column", "column", "row"],
    })
  ),
  infoContainer: css(
    {
      display: "flex",
      flexDirection: "column",
      marginBottom: "var(--space-4)",
      width: "100%",
    },
    mq({
      gap: ["var(--space-2)", "var(--space-2)"],
      paddingLeft: ["var(--space-4)", "var(--space-4)", "var(--space-8)"],
      paddingTop: "var(--space-5)",
    })
  ),
  titleRow: css(
    {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "var(--space-4)",
      width: "100%",
      flexWrap: "nowrap",
    },
    mq({
      marginTop: [0, 0, "var(--space-2)"],
    })
  ),
  titleText: css({
    flex: "1 1 auto",
    minWidth: 0,
  }),
  menuContainer: css(
    {
      flex: "0 0 auto",
    },
    mq({
      paddingRight: ["var(--space-4)", "var(--space-8)"],
    })
  ),
  profileCard: css(
    {
      display: "flex",
      flexWrap: "wrap",
      gap: "var(--space-2)",
    },
    mq({
      alignItems: ["normal", "normal", "center"],
      flexDirection: ["column", "row"],
    })
  ),
  innerProfileCard: css(
    {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "wrap",
    },
    mq({
      gap: ["var(--space-2)", "var(--space-4)"],
    })
  ),
  requestor: css({
    display: "flex",
    flexDirection: "row",
    gap: "var(--space-1)",
    alignItems: "baseline",
  }),
};
